// getFormattedValue.ts

import { get as dotGet } from 'lodash';
import { getFieldOptions } from '../formHandling';
import { TFormData, TJourneyConfig } from '../types';


interface YesNoLabels {
  yes: string;
  no: string;
}

interface GetFormattedValueOptions {
  maxPasses?: number;
  unresolved?: 'keep' | 'na' | 'empty';
  yesNo?: YesNoLabels;
}

interface Ctx {
  config: TJourneyConfig;
  formData: TFormData;
  opts: Required<GetFormattedValueOptions>;
}

const TOKEN_RE = /\$\{([^}]+)\}/g;
const TOKEN_PEEK = /\$\{[^}]+}/;

const normaliseOptions = (options?: GetFormattedValueOptions) => ({
  maxPasses: options?.maxPasses ?? 5,
  unresolved: options?.unresolved ?? 'na',
  yesNo: options?.yesNo ?? { yes: 'Yes', no: 'No' },
});

const hasTokens = (str: string) => TOKEN_PEEK.test(str);

const handleUnresolved = (str: string, mode: 'keep' | 'na' | 'empty') => {
  if (mode === 'na') return str.replace(TOKEN_RE, 'N/A');
  if (mode === 'empty') return str.replace(TOKEN_RE, '');
  return str; // keep as-is
};

const findFieldByName = (config: TJourneyConfig, name: string): any | null => {
  const findFieldInList = (list: any[] | undefined, n: string): any | null => {
    if (!Array.isArray(list)) return null;
    for (const f of list) {
      if (f?.name === n) return f;
      const nested = findFieldInList(f?.fields, n);
      if (nested) return nested;
    }
    return null;
  };
  for (const s of config?.sections ?? []) {
    const found = findFieldInList(s?.fields, name);
    if (found) return found;
  }
  return null;
};

const tryGetOptions = (field: any, formData: TFormData) => {
  if (typeof getFieldOptions === 'function') {
    return getFieldOptions(field, formData);
  }
  return field?.options ?? [];
};

const mapViaOptionsIfPossible = (value: any, field: any, ctx: Ctx): string | undefined => {
  if (!field || (!field.options && !field.dataSource)) return undefined;
  const opts = tryGetOptions(field, ctx.formData);
  if (!opts?.length) return undefined;

  if (typeof value === 'string' || typeof value === 'number') {
    const found = opts.find((o: any) => o.value === value || o.label === value);
    return found ? found.label : undefined;
  }

  if (Array.isArray(value)) {
    const labels = value
      .map((v: any) => {
        if (typeof v === 'string' || typeof v === 'number') {
          const found = opts.find((o: any) => o.value === v);
          return found ? found.label : undefined;
        }
        if (v?.value) {
          const found = opts.find((o: any) => o.value === v.value);
          return found ? found.label : undefined;
        }
        return undefined;
      })
      .filter((l: any) => l !== undefined);

    return labels.length ? labels.join(', ') : undefined;
  }
  return undefined;
};

const stringifyArray = (arr: any[], field: any, ctx: Ctx) => {
  const viaOptions = mapViaOptionsIfPossible(arr, field, ctx);
  if (viaOptions) return viaOptions;

  if (arr.every((item) => typeof item !== 'object')) {
    return arr.join(', ');
  }
  if (arr.every((item) => typeof item === 'object' && 'value' in item)) {
    return arr.map((i) => i.value).join(', ');
  }
  return String(arr.length);
};

const stringifyObject = (obj: Record<string, any>) => {
  return Object.values(obj).join(', ');
};

const formatScalar = (value: any, field: any, ctx: Ctx) => {
  if (!field) return String(value);

  // Currency / money
  if (
    field.variant === 'money' ||
    (field.type === 'currency' && (field?.params?.adornment?.start === '£' || field?.params?.adornment?.end === '£'))
  ) {
    if (isNaN(Number(value))) return String(value);
    return '£' + Number(value).toLocaleString('en-GB');
  }

  // Try options mapping last
  const viaOptions = mapViaOptionsIfPossible(value, field, ctx);
  if (viaOptions) return viaOptions;

  return String(value);
};

const formatValue = (raw: any, fieldName: string, ctx: Ctx): string => {
  const field = findFieldByName(ctx.config, fieldName);

  if (typeof raw === 'boolean') {
    return raw ? ctx.opts.yesNo.yes : ctx.opts.yesNo.no;
  }
  if (Array.isArray(raw)) return stringifyArray(raw, field, ctx);
  if (raw && typeof raw === 'object') {
    return stringifyObject(raw);
  }
  return formatScalar(raw, field, ctx);
};

const resolveExpression = (expr: string, values: any, ctx: Ctx): string | undefined => {
  const parts = expr.split('||').map((s) => s.trim());
  for (const p of parts) {
    const lit = p.match(/^['"](.*)['"]$/);
    if (lit) return lit[1];

    const val = dotGet(values, p);
    if (val === false || val === 0 || (val !== null && val !== undefined && val !== '')) {
      return formatValue(val, p, ctx);
    }
  }
  return undefined;
};

const replaceTokensOnce = (str: string, ctx: Ctx, values: any) => {
  let changed = false;
  const out = str.replace(TOKEN_RE, (_m, inner) => {
    const resolved = resolveExpression(inner, values, ctx);
    if (resolved === undefined) return `\${${inner}}`;
    changed = true;
    return String(resolved);
  });
  return { out, changed };
};

export const getFormattedValue = (
  config: TJourneyConfig,
  template: string,
  formData: TFormData,
  options?: GetFormattedValueOptions,
): string => {
  const { values } = formData || {};
  const opts = normaliseOptions(options);
  const ctx: Ctx = { config, formData, opts };
  

  let result = String(template ?? '');
  let pass = 0;

  while (pass < opts.maxPasses && hasTokens(result)) {
    const { out, changed } = replaceTokensOnce(result, ctx, values);
    result = out;
    pass++;
    if (!changed) break;
  }

  if (hasTokens(result)) {
    result = handleUnresolved(result, opts.unresolved);
  }
  return result;
};
